import { useMemo } from 'react';
import type { CreationFilmMaterialV1, CreationFilmRunV1 } from '@djgg/creations';
import { useSubscription } from '../hooks/useSubscription.js';
import { useServices } from '../services/context.js';
import { selectCreationFilmRun, type CreationFilmAuthority } from './creationFilmModel.js';

/** The one live feed of a creation's film: its followed run and that run's materials. */
export function useCreationFilm(
  creationId: string | null,
  enabled: boolean,
  authority: CreationFilmAuthority,
  initialRun?: CreationFilmRunV1 | null,
): { run: CreationFilmRunV1 | null; materials: CreationFilmMaterialV1[] } {
  const services = useServices();
  const runs = useSubscription<CreationFilmRunV1[]>(
    cb => {
      if (!enabled || !creationId) {
        cb([]);
        return () => {};
      }
      return services.subscribe.creationFilmRuns(creationId, cb);
    },
    [], [creationId, enabled]);
  const authorityKey = JSON.stringify(authority);
  const availableRuns = useMemo(() => {
    if (!initialRun || runs.some(run => run.runId === initialRun.runId)) return runs;
    return [initialRun, ...runs];
  }, [runs, initialRun]);
  const run = useMemo(() => selectCreationFilmRun(availableRuns, authority), [availableRuns, authorityKey]);
  const materials = useSubscription<CreationFilmMaterialV1[]>(
    cb => {
      if (!enabled || !run) {
        cb([]);
        return () => {};
      }
      return services.subscribe.creationFilmMaterials(run.runId, cb);
    },
    [], [enabled, run?.runId]);
  return { run, materials };
}
